import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

const studyTasks = [
  {
    icon: '/images/xuexi_enwuyi.png',
    label: '任务一',
    sublabel: '邀请三位',
    title: '邀请三位及党政学习打卡 5 日',
    reward: '补助金 30000 元 + 党政钱包资金 300 元 +\n党政钱包提现额度 10',
  },
  {
    icon: '/images/xuexi_renwuer.png',
    label: '任务二',
    sublabel: '邀请十位',
    title: '邀请十位及党政学习打卡 8 日',
    reward: '补助金 80000 元 + 党政钱包资金 1000 元 +\n党政钱包提现额度 20',
  },
  {
    icon: '/images/xuexi_renwusan.png',
    label: '任务三',
    sublabel: '邀请三十位',
    title: '邀请三十位及党政学习打卡 15 日',
    reward: '补助金 300000 元 + 党政钱包资金 5000 元 +\n党政钱包全额提现权限',
  },
];

interface TaskStatus {
  id: number;
  achieved: boolean;
  rewarded: boolean;
}

export default function StudyPage() {
  const navigate = useNavigate();
  const [totalDays, setTotalDays] = useState(0);
  const [checkedToday, setCheckedToday] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskStatus[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showWithdrawNotice, setShowWithdrawNotice] = useState(false);
  const [totalInvites, setTotalInvites] = useState(0);

  useEffect(() => {
    api.get('/study/status').then(({ data }) => {
      setTotalDays(data?.total_days ?? 0);
      setCheckedToday(!!data?.checked_today);
      if (Array.isArray(data?.tasks)) setTasks(data.tasks);
    }).catch(() => { });
    api.get('/study/video').then(({ data }) => {
      if (data?.video_url) setVideoUrl(data.video_url);
      if (data?.poster_url) setPosterUrl(data.poster_url);
    }).catch(() => { });
    api.get('/withdraw-quota/status').then(({ data }) => {
      setTotalInvites(data?.total_invites ?? 0);
      if (!data?.is_unlimited) {
        setShowWithdrawNotice(true);
      }
    }).catch(() => { });
  }, []);

  function openVideoModal() {
    setVideoEnded(false);
    setShowModal(true);
  }

  function handleVideoEnded() {
    setVideoEnded(true);
  }

  function closeModal() {
    setShowModal(false);
    setVideoEnded(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }

  async function handleCheckin() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/study/checkin');
      toast.success(data?.message ?? '学习打卡成功');
      setTotalDays(data?.total_days ?? totalDays + 1);
      setCheckedToday(true);
      if (Array.isArray(data?.tasks)) setTasks(data.tasks);
      closeModal();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '打卡失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto min-h-screen" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Header */}
      <div className="relative">
        <img src="/images/xuexi_shang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Video section */}
      <div className="px-4 -mt-4 relative z-10">
        <div className="bg-[#fff8f0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <img src={"/images/xuexi_guankan.png"} alt="xuexi_guankan" className="w-[180px]" />
          </div>

          {/* Video thumbnail - click to open modal */}
          <div
            onClick={videoUrl ? openVideoModal : undefined}
            className={`bg-black rounded-xl overflow-hidden relative ${videoUrl ? 'cursor-pointer' : ''}`}
            style={{ aspectRatio: '16/9' }}
          >
            {posterUrl ? (
              <img src={posterUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-white/30 text-sm">{videoUrl ? '点击播放' : '暂无学习视频'}</span>
              </div>
            )}
            {videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#c4161c"><path d="M8 5v14l11-7z" /></svg>
                </div>
              </div>
            )}
          </div>

          {/* Watch & checkin button */}
          <div className="mt-3 text-center">
            {!checkedToday ? (
              <button onClick={openVideoModal} className="">
                <img src={"/images/xuexi_liji.png"} alt="xuexi_liji" className="max-w-[330px] mx-auto" />
              </button>
            ) : (
              <button
                disabled={true}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#c4161c] to-[#8b0000] text-white text-sm font-bold px-6 py-2 rounded-full disabled:opacity-50"
              >
                今日已打卡
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Study stats */}
      <div className="px-4 mt-3">
        <div className="bg-[#fff8f0] rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/images/xuexi_leiji.png" alt="" className="w-6 h-6" />
              <span className="text-[#c4161c] font-bold text-base">已累计打卡学习：{totalDays}天</span>
            </div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${checkedToday ? 'bg-green-100 text-green-700' : 'bg-[#c4161c] text-[#fef100]'}`}>
              {checkedToday ? '已打卡' : '未打卡'}
            </span>
          </div>

          <div className="mt-3 mb-2 text-center">
            <img src={"/images/xuexi_henxgian.png"} alt="xuexi_henxgian" className="w-full" />
          </div>

          <div className="mt-3 flex items-center gap-2">
            <img src="/images/xuexi_meiri.png" alt="xuexi_meiri" className="max-w-[350px]" />
          </div>
        </div>
      </div>

      {/* Task cards */}
      <div className="px-4 mt-3 space-y-3">
        {studyTasks.map((task, idx) => {
          const taskStatus = tasks.find(t => t.id === idx + 1);
          const achieved = taskStatus?.achieved ?? false;
          return (
            <div key={idx} className="relative">
              <img src={task.icon} alt="" className="w-full" />
              <div className="absolute top-4 right-4">
                {achieved ? (
                  <span className="text-[10px] font-bold text-white border border-green-300 px-2 py-0.5 rounded-full bg-green-600">
                    已达标
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-white border border-gray-300 px-2 py-0.5 rounded-full bg-[#b91210]">
                    未达标
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom decoration */}
      <img src="/images/xuexi_xia.png" alt="" className="w-full" />

      {/* Video Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-xl w-[100%] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-bold text-gray-800">观看学习视频</h3>
              <button onClick={closeModal} className="text-gray-400 text-lg">&times;</button>
            </div>

            <div className="bg-black" style={{ aspectRatio: '16/9' }}>
              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={posterUrl ?? undefined}
                  controls
                  autoPlay
                  playsInline
                  onEnded={handleVideoEnded}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white/30 text-sm">暂无学习视频</span>
                </div>
              )}
            </div>

            {!checkedToday && (
              <div className="p-4 text-center">
                {!videoEnded && videoUrl ? (
                  <p className="text-xs text-gray-500">请观看完整视频后打卡</p>
                ) : (
                  <button
                    onClick={handleCheckin}
                    disabled={submitting}
                    className="bg-gradient-to-r from-[#c4161c] to-[#8b0000] text-white text-sm font-bold px-8 py-2 rounded-full disabled:opacity-50"
                  >
                    {submitting ? '打卡中...' : '确认打卡'}
                  </button>
                )}
                {!videoUrl && (
                  <button
                    onClick={handleCheckin}
                    disabled={submitting}
                    className="bg-gradient-to-r from-[#c4161c] to-[#8b0000] text-white text-sm font-bold px-8 py-2 rounded-full disabled:opacity-50 mt-2"
                  >
                    {submitting ? '打卡中...' : '确认打卡'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Withdraw Notice Modal */}
      {showWithdrawNotice && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="relative max-w-md w-[90%] max-h-[90vh] flex flex-col items-center">
            <button
              onClick={() => setShowWithdrawNotice(false)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-gray-600 text-xl font-bold shadow-lg"
            >
              &times;
            </button>
            <img
              src="/images/notice_withdraw.png"
              alt="提现通知"
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="text-center mt-3 shrink-0">
              <span className="text-white font-bold text-lg">
                已邀请：<span className="text-[#fef100] text-2xl">{totalInvites}</span>位
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
